import { useCallback, useEffect, useState } from 'react'

/**
 * Parameterized dark/light theme hook — the shared primitive behind both
 * `useAdminTheme` and `useOwnerTheme`. Each app supplies its own storage key
 * and <html> attribute name so the two never collide:
 *
 *   admin → { storageKey: 'naya:admin-theme', attr: 'data-admin-theme', default: 'dark' }
 *   owner → { storageKey: 'naya:owner-theme', attr: 'data-owner-theme', default: 'light' }
 *
 * The choice persists in localStorage; the attribute is written to <html> so
 * a CSS scope (`.admin-root` / `.owner-root`) can switch palettes on it.
 *
 * Lives in apps/web/src/shared/ (not packages/shared) — see CLAUDE.md "Shared
 * code locations": packages/shared is pure-TS, no React/DOM; cross-app
 * web-only code lives here.
 */

export type Theme = 'dark' | 'light'

export interface ThemeConfig {
  /** localStorage key the choice persists under. */
  storageKey: string
  /** <html> attribute the current theme is written to (e.g. 'data-owner-theme'). */
  attr: string
  /** Theme used when nothing is stored / storage is unavailable. */
  defaultTheme: Theme
}

function readInitial(config: ThemeConfig): Theme {
  try {
    const saved = localStorage.getItem(config.storageKey)
    if (saved === 'dark' || saved === 'light') return saved
  } catch {
    // localStorage unavailable (private mode) — fall through to default.
  }
  return config.defaultTheme
}

/**
 * Applies the saved theme to <html> on mount without subscribing to changes.
 * Call once high in the app tree so the attribute is set on every route —
 * including public pages (login) that don't mount the ThemeToggle.
 */
export function useApplyTheme(config: ThemeConfig): void {
  useEffect(() => {
    document.documentElement.setAttribute(config.attr, readInitial(config))
    // config is a stable literal per app; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Stateful theme hook for the header ThemeToggle: tracks the current theme,
 * writes the <html> attribute + localStorage on change, exposes a toggle.
 */
export function useTheme(config: ThemeConfig): {
  theme: Theme
  toggle: () => void
} {
  const [theme, setTheme] = useState<Theme>(() => readInitial(config))

  useEffect(() => {
    document.documentElement.setAttribute(config.attr, theme)
    try {
      localStorage.setItem(config.storageKey, theme)
    } catch {
      // in-memory only if storage is blocked
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, toggle }
}
